/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action } from "mobx";
import * as constants from "constants/constants";
import { createDeferredPromiseObject } from "utils/DeferredPromise";

class OverlayDropinStore {
  @observable shouldRenderCommentsDropin;
  @observable shouldRenderPersonalCommentsDropin;
  @observable shouldEnableVoiceAnnotations;
  @observable shouldUseEncryptedWrite;
  @observable shouldRenderHighlightsDropin;
  @observable overlayDropins;
  @observable pageOverlays;
  @observable cursorChanged;
  @observable currentCursor;
  @observable shouldRenderEditDropin;
  @observable isDemoMode;
  @observable showEditOnTextSelection;
  @observable shouldRenderCropPagesDropin;
  @observable shouldRenderTroveDropin;
  @observable shouldRenderFillSignDropin;
  @observable shouldRenderPdfRequestSignaturesDropin;
  @observable shouldRenderVisualizerDropin;
  @observable shouldRenderSignAuthoringDropin;
  @observable shouldRenderSignAuthoring2Dropin;
  @observable shouldRenderESignOverlayDropin;
  @observable isDropinLoaded;
  @observable isDropinLoading;
  @observable overlayUpdatedTimestamp;
  @observable shouldRenderNumberPagesDropin;
  @observable shouldEnableImageSelection;
  @observable shouldDeferLoadOverlays;
  shouldPreloadEditModule;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    this.shouldRenderCommentsDropin = false;
    this.shouldRenderPersonalCommentsDropin = false;
    this.shouldEnableVoiceAnnotations = false;
    this.shouldUseEncryptedWrite = false;
    this.shouldRenderHighlightsDropin = false;
    this.overlayDropins = [];
    this.pageOverlays = [];
    this.cursorChanged = false;
    this.currentCursor = "";
    this.shouldRenderEditDropin = constants.EDIT_DROPIN_TYPE.NONE;
    this.isDemoMode = false;
    this.showEditOnTextSelection = true;
    this.shouldRenderCropPagesDropin = false;
    this.shouldRenderTroveDropin = false;
    this.shouldRenderFillSignDropin = false;
    this.shouldRenderSignAuthoringDropin = false;
    this.shouldRenderSignAuthoring2Dropin = false;
    this.shouldRenderVisualizerDropin = false;
    this.shouldRenderSignAuthoringDropin = false;
    this.shouldRenderESignOverlayDropin = false;
    this.shouldEnableImageSelection = false;
    this.shouldPreloadEditModule = false;
    this.isDropinLoading = new Map();
    this.isDropinLoaded = new Map();
    this.dropinLoadedPromises = new Map();
    this.acroformDropinMountedPromise = createDeferredPromiseObject();
    this.overlaysVisibilityMap = new Map();
    this.overlayUpdatedTimestamp = 0;
    this.editDropinMountedPromise = createDeferredPromiseObject();
    // The deferred promise will get rejected if full edit dropin is not mounted.
    // To prevent any potential issues due to unhandled rejections, we are adding a catch handler.
    // No specific action needs to be taken within the catch handler.
    this.editDropinMountedPromise.promise.catch(() => {});
    this.shouldDeferLoadOverlays = false;
  }

  @action.bound
  setShouldRenderCommentsDropin(shouldRenderCommentsDropin) {
    this.shouldRenderCommentsDropin = shouldRenderCommentsDropin;
  }

  @action.bound
  setShouldRenderPersonalCommentsDropin(shouldRenderPersonalCommentsDropin) {
    this.shouldRenderPersonalCommentsDropin = shouldRenderPersonalCommentsDropin;
  }

  @action.bound
  setShouldEnableVoiceAnnotations(shouldEnableVoiceAnnotations) {
    this.shouldEnableVoiceAnnotations = shouldEnableVoiceAnnotations;
  }

  @action.bound
  setShouldUseEncryptedWrite(enableEncryptedWrite) {
    this.shouldUseEncryptedWrite = enableEncryptedWrite;
  }

  @action.bound
  setShouldRenderEditDropin(enableEdit) {
    this.shouldRenderEditDropin = enableEdit;
  }

  setShouldPreloadEditModule(shouldPreloadEditModule) {
    this.shouldPreloadEditModule = shouldPreloadEditModule;
  }

  @action.bound
  setIsDemoMode(value) {
    this.isDemoMode = value;
  }

  @action.bound
  setShowEditOnTextSelection(value) {
    this.showEditOnTextSelection = value;
  }

  @action.bound
  setShouldRenderCropPagesDropin(enableCropPages) {
    this.shouldRenderCropPagesDropin = enableCropPages;
  }

  @action.bound
  setShouldRenderNumberPagesDropin(enableNumberPages) {
    this.shouldRenderNumberPagesDropin = enableNumberPages;
  }

  @action.bound
  setShouldRenderTroveDropin(shouldRenderTroveDropin) {
    this.shouldRenderTroveDropin = shouldRenderTroveDropin;
  }

  @action.bound
  setShouldRenderFillSignDropin(enableFillSign) {
    this.shouldRenderFillSignDropin = enableFillSign;
  }

  @action.bound
  setShouldRenderPdfRequestSignaturesDropin(enablePdfRequestSignatures) {
    this.shouldRenderPdfRequestSignaturesDropin = enablePdfRequestSignatures;
  }

  @action.bound
  setShouldRenderVisualizerDropin(enableVisualizer) {
    this.shouldRenderVisualizerDropin = enableVisualizer;
  }

  @action.bound
  setShouldRenderSignAuthoringDropin(shouldRenderSignAuthoringDropin) {
    this.shouldRenderSignAuthoringDropin = shouldRenderSignAuthoringDropin;
  }

  @action.bound
  setShouldRenderSignAuthoring2Dropin(shouldRenderSignAuthoring2Dropin) {
    this.shouldRenderSignAuthoring2Dropin = shouldRenderSignAuthoring2Dropin;
  }

  @action.bound
  setShouldRenderESignOverlayDropin(shouldRenderESignOverlayDropin) {
    this.shouldRenderESignOverlayDropin = shouldRenderESignOverlayDropin;
  }
  @action.bound
  setOverlayDropinLoaded(dropinName, isLoaded) {
    this.isDropinLoaded = Object.assign({}, this.isDropinLoaded);
    this.isDropinLoaded[dropinName] = isLoaded;
  }

  @action.bound
  setOverlayDropinLoading(dropinName, isLoading) {
    this.isDropinLoading = Object.assign({}, this.isDropinLoading);
    this.isDropinLoading[dropinName] = isLoading;
  }

  @action.bound
  setOverlayDropinLoadedPromise(dropinName, promise) {
    this.dropinLoadedPromises.set(dropinName, promise);
  }

  @action.bound
  resetOverlayDropinLoadedPromise(dropinName) {
    this.dropinLoadedPromises.delete(dropinName);
  }

  @action.bound
  setShouldRenderHighlightsDropin(isProd, shouldRenderHighlightsDropin) {
    // Do not show highlights in prod
    if (isProd) {
      this.shouldRenderHighlightsDropin = false;
    } else {
      this.shouldRenderHighlightsDropin = shouldRenderHighlightsDropin;
    }
  }

  @action.bound
  addOverlayDropin(dropinName, dropin, api, config) {
    this.overlayDropins.push({
      dropinName,
      dropin,
      api,
      config
    });
  }

  @action.bound
  removeOverlayDropin(dropin) {
    this.overlayDropins = this.overlayDropins.filter(item => item.dropinName !== dropin);
  }

  @action.bound
  addPageOverlay(overlay) {
    overlay.hasRequestedForFront = false;
    if (overlay.clientName === "COMMENT") {
      // find the index of first overlay which requested to be in front.
      // if no overlay found then add 'COMMENT' to last else add before first requestor.
      const index = this.pageOverlays.findIndex(element => element.hasRequestedForFront);
      if (index === -1) {
        this.pageOverlays.push(overlay);
      } else {
        this.pageOverlays.splice(index, 0, overlay);
      }
    } else {
      // Precautionary check to remove overlay if already present and push the overlay to the start of array
      // This avoids issues due to multiple overlays of same clientname
      const index = this.pageOverlays.findIndex(element => element.clientName === overlay.clientName);
      if (index > -1) {
        this.pageOverlays.splice(index, 1);
      }
      this.pageOverlays.unshift(overlay);
    }
  }

  @action.bound
  removePageOverlay(clientName) {
    const index = this.pageOverlays.findIndex(element => element.clientName === clientName);
    if (index > -1) {
      this.pageOverlays.splice(index, 1);
    }
  }

  @action.bound
  sendOverlayToBack(clientName) {
    const index = this.pageOverlays.findIndex(element => element.clientName === clientName);
    if (index !== -1) {
      const [overlay] = this.pageOverlays.splice(index, 1);
      overlay.hasRequestedForFront = false;
      this.pageOverlays.unshift(overlay);
    }
  }

  @action.bound
  sendOverlayToFront(clientName) {
    const index = this.pageOverlays.findIndex(element => element.clientName === clientName);
    if (index !== -1) {
      const [overlay] = this.pageOverlays.splice(index, 1);
      overlay.hasRequestedForFront = true;
      this.pageOverlays.push(overlay);

      if (
        clientName !== constants.EDIT_CLIENT_NAME &&
        clientName !== constants.CROP_CLIENT_NAME &&
        clientName !== constants.NUMBERPAGE_CLIENT_NAME
      ) {
        const acroformOverlayIndex = this.pageOverlays.findIndex(
          element => element.clientName === constants.ACROFORM_CLIENT_NAME
        );
        if (acroformOverlayIndex !== -1) {
          const [acroformOverlay] = this.pageOverlays.splice(acroformOverlayIndex, 1);
          acroformOverlay.hasRequestedForFront = true;
          this.pageOverlays.push(acroformOverlay);
        }
      }
    }
  }

  @action.bound
  setEnableImageSelectionConfig(shouldEnableImageSelection) {
    this.shouldEnableImageSelection = shouldEnableImageSelection;
  }

  @action
  cursorStateChange(value) {
    this.cursorChanged = value;
  }

  @action
  setCurrentCursor(cursor) {
    this.currentCursor = cursor;
  }

  @action
  setOverlayVisibility(clientName, visibility) {
    if (this.overlaysVisibilityMap.get(clientName) !== visibility) {
      this.overlaysVisibilityMap.set(clientName, visibility);
      // This will update the time when field was updates, so the change will be picked by page canvas to re render the latest changes
      this.overlayUpdatedTimestamp = new Date().getTime();
    }
  }

  @action
  setShouldDeferLoadOverlays(val) {
    this.shouldDeferLoadOverlays = val;
  }
}

export { OverlayDropinStore };
